import {
  AsYouType,
  CountryCode,
  getCountries,
  getCountryCallingCode,
  isValidPhoneNumber,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';

export const DEFAULT_COUNTRY: CountryCode = 'EG';

export function getCountryFlag(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

export function parsePhoneFields(value?: string, fallbackCountry: CountryCode = DEFAULT_COUNTRY) {
  if (!value) {
    return { country: fallbackCountry, nationalNumber: '' };
  }

  const parsed = parsePhoneNumberFromString(value);
  if (parsed) {
    return {
      country: (parsed.country ?? fallbackCountry) as CountryCode,
      nationalNumber: parsed.nationalNumber,
    };
  }

  return { country: fallbackCountry, nationalNumber: value.replace(/\D/g, '') };
}

export function formatNationalInput(country: CountryCode, input: string): string {
  const formatter = new AsYouType(country);
  return formatter.input(input.replace(/\D/g, ''));
}

export function buildPhoneE164(country: CountryCode, nationalNumber: string): string {
  const digits = nationalNumber.replace(/\D/g, '');
  if (!digits) return '';

  const parsed = parsePhoneNumberFromString(digits, country);
  return parsed?.number ?? `+${getCountryCallingCode(country)}${digits}`;
}

export function isOptionalPhoneValid(value?: string): boolean {
  if (!value) return true;
  return isValidPhoneNumber(value);
}

export function getCountryOptions() {
  const displayNames = new Intl.DisplayNames(['en'], { type: 'region' });

  const options = getCountries().map((code) => ({
    code,
    callingCode: getCountryCallingCode(code),
    name: displayNames.of(code) ?? code,
    flag: getCountryFlag(code),
  }));

  options.sort((a, b) => a.name.localeCompare(b.name));

  const egyptIndex = options.findIndex((option) => option.code === DEFAULT_COUNTRY);
  if (egyptIndex > 0) {
    const [egypt] = options.splice(egyptIndex, 1);
    options.unshift(egypt);
  }

  return options;
}
